import React, { useEffect, useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import './IndustryDashboard.css';
import Jodit from '../../../components/JoditEditor/Jodit';
import { Industry } from '../../../../data_types';
import { fetchIndustries } from '../../../backend/industries';
import { Link } from 'react-router-dom';

const IndustryDashboard = () => {
    const [newIndustryTitle, setNewIndustryTitle] = useState('');
    const [newIndustryDescription, setNewIndustryDescription] = useState('');
    const [newIndustryVideo, setNewIndustryVideo] = useState<File | null>(null);
    const [deleteIndustryId, setDeleteIndustryId] = useState('');
    const [industriesPerRow, setIndustriesPerRow] = useState('');

    const industriesBaseUrl = process.env.REACT_APP_INDUSTRIES_BASE_URL; // Base URL for the API server

    const [data, setData] = useState<{ industries: Industry[] }>({ industries: [] });

    useEffect(() => {
        const loadIndustries = async () => {
            const fetchedData = await fetchIndustries();
            setData(fetchedData);
        };
        loadIndustries();
    }, []);

    const handleAddIndustrySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!newIndustryTitle || !newIndustryDescription || !newIndustryVideo) {
            alert('Please provide all required fields for the industry.');
            return;
        }

        const formData = new FormData();
        formData.append('industryTitle', newIndustryTitle);
        formData.append('industryDescription', newIndustryDescription);
        formData.append('video', newIndustryVideo);

        try {
            const response = await fetch(`${industriesBaseUrl}`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });

            if (response.ok) {
                alert('Industry added successfully');
                setNewIndustryTitle('');
                setNewIndustryDescription('');
                setNewIndustryVideo(null);
                window.location.reload();
            } else {
                alert('Failed to add industry');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error adding industry');
        }
    };

    const handleDeleteIndustrySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!deleteIndustryId) {
            alert('Please provide the industry ID to delete.');
            return;
        }

        try {
            const response = await fetch(`${industriesBaseUrl}/${deleteIndustryId}`, {
                method: 'DELETE',
                credentials: 'include',
            });

            if (response.ok) {
                alert('Industry deleted successfully');
                setDeleteIndustryId('');
                window.location.reload();
            } else {
                alert('Failed to delete industry');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error deleting industry');
        }
    };

    const handleUpdateIndustriesPerRow = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!industriesPerRow || isNaN(Number(industriesPerRow))) {
            alert('Please provide a valid number for industries per row.');
            return;
        }

        try {
            const response = await fetch(`${industriesBaseUrl}/updateIndustriesPerRow`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ industriesPerRow: industriesPerRow }),
                credentials: 'include',
            });

            if (response.ok) {
                alert('industriesPerRow updated successfully');
                setIndustriesPerRow('');
            } else {
                alert('Failed to update industriesPerRow');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error updating industriesPerRow');
        }
    };

    return (
        <Box className="industry-dashboard">
            <form onSubmit={handleAddIndustrySubmit}>
                <h2>Add a New Industry</h2>
                <TextField
                    label="Industry Title"
                    value={newIndustryTitle}
                    onChange={(e) => setNewIndustryTitle(e.target.value)}
                    fullWidth
                    required
                />
                <Jodit placeholder={"Your text here"} value={newIndustryDescription} onChange={setNewIndustryDescription} />
                <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setNewIndustryVideo(e.target.files?.[0] || null)}
                    required
                />
                <Button type="submit" variant="contained" color="primary">
                    Add Industry
                </Button>
            </form>

            <form onSubmit={handleDeleteIndustrySubmit}>
                <h2>Delete an Industry</h2>
                <FormControl fullWidth required>
                    <InputLabel id="type-label">Type</InputLabel>
                    <Select
                        labelId="type-label"
                        name="type"
                        value={deleteIndustryId}
                        onChange={(e) => setDeleteIndustryId(e.target.value)}
                    >
                        {data?.industries?.length > 0 ? (
                            data?.industries.map((industry) => (
                                <MenuItem value={industry.id}>{industry.industryTitle}</MenuItem>
                            ))
                        ) : (
                            <p>No industries available</p>
                        )}
                    </Select>
                </FormControl>
                <Button type="submit" variant="contained" color="secondary">
                    Delete Industry
                </Button>
            </form>
            <form onSubmit={handleUpdateIndustriesPerRow}>
                <h2>Update Industries Per Row</h2>
                <TextField
                    label="Industries Per Row"
                    value={industriesPerRow}
                    onChange={(e) => setIndustriesPerRow(e.target.value)}
                    fullWidth
                    required
                />
                <Button type="submit" variant="contained" color="primary">
                    Update Industries Per Row
                </Button>
            </form>
        </Box>
    );
};

export default IndustryDashboard;